import { EVENT_SITES } from '@crossbeam/itly';

import axios from 'axios';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import useIteratively from '@/composables/useIteratively';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import { captureException } from '@/errors';
import {
  useFeatureFlagStore,
  useFlashesStore,
  usePartnerSuggestionsStore,
  usePartnersStore,
} from '@/stores';
import urls from '@/urls';
import { sortByKey } from '@/utils';

export default function usePartnerManagement() {
  const route = useRoute();

  const featureFlagStore = useFeatureFlagStore();
  const partnersStore = usePartnersStore();
  const partnerSuggestionsStore = usePartnerSuggestionsStore();
  const flashesStore = useFlashesStore();

  const { iteratively } = useIteratively();

  const fromPartner = computed(() => route.query.partner_suggestion === 'true');
  const fromUniversal = computed(
    () => route.query.universal_suggested === 'true',
  );

  const getDiscoverableTeam = async ({ publicInviteCode }) => {
    try {
      const url = urls.prospectivePartnerTeam.byInviteCode(publicInviteCode);
      const response = await axios.get(url);
      const prospectivePartnerTeam = response.data.items
        .map((partner) => {
          const name = `${partner.first_name} ${partner.last_name}`;
          return {
            ...partner,
            name,
            label: name,
            value: partner.id,
          };
        })
        .sort(sortByKey('name'));
      return prospectivePartnerTeam;
    } catch (error) {
      const msg = error.message;
      flashesStore.addErrorFlash(msg);
    }
  };

  function makePayload({
    company,
    contact,
    inviteMessage,
    onBehalfOf,
    activationTeamCc,
  }) {
    const payload = {};

    payload.from_partner_suggestion = fromPartner.value || fromUniversal.value;
    payload.invite_message = inviteMessage;
    payload.on_behalf_of = onBehalfOf;
    if (featureFlagStore.hasFeatureFlag(FEATURE_FLAG.TEMP_ACTIVATION_TEAM_CC)) {
      payload.activation_team_cc = activationTeamCc;
    }

    /* Discoverable organizations + users */
    if (company.public_invite_code && contact.first_name && contact.last_name) {
      return {
        ...payload,
        public_invite_code: company.public_invite_code,
        contact_first_name: contact.first_name,
        contact_last_name: contact.last_name,

        contact_name: `${contact.first_name} ${contact.last_name}`,
      };
    }

    /* Discoverable organization only */
    if (company.public_invite_code) {
      return {
        ...payload,
        public_invite_code: company.public_invite_code,
        ...(contact.email ? { contact_email: contact.email } : {}),
      };
    }

    /* Neither organization nor users are discoverable */
    return {
      ...payload,
      contact_company: company.name,
      contact_email: contact.email,
      clearbit_domain: company.domain,
      contact_name: null,
    };
  }

  function iterativelyHandler({ inviteMessage }) {
    let inviteSource = 'partner invite form';
    if (fromUniversal.value) inviteSource = 'universal suggested';
    if (fromPartner.value) inviteSource = 'partner suggestion';

    const iterativelyPayload = {
      invite_type: inviteSource,
      invite_message: inviteMessage || '',
      event_site: fromUniversal.value
        ? EVENT_SITES.UNIVERSAL_SEARCH_RESULT
        : EVENT_SITES.PARTNER_PROPOSAL_MODAL,
    };
    iteratively.userSentPartnerInvite(iterativelyPayload);
  }

  const sendInvitation = async ({
    company,
    contact,
    inviteMessage,
    onBehalfOf = null,
    activationTeamCc = false,
    skipFlash = false,
    skipRefresh = false,
  }) => {
    try {
      const url = urls.proposals.create_v0_4;
      const payload = makePayload({
        company,
        contact,
        inviteMessage,
        onBehalfOf,
        activationTeamCc,
      });
      await axios.post(url, payload);
      iterativelyHandler({ inviteMessage });
      if (!skipFlash) {
        flashesStore.addSuccessFlash({ message: 'Invitation sent' });
      }
      if (!skipRefresh) {
        await Promise.all([
          partnersStore.refreshPartnersStore(),
          partnerSuggestionsStore.refreshPartnerSuggestions(),
        ]);
      }
      return true;
    } catch (err) {
      flashesStore.addUnhandledError(err);
      return false;
    }
  };

  async function createOfflinePartner({ company, website }) {
    try {
      const { data } = await axios.post(urls.offlinePartners.create, {
        organization: {
          name: company.name,
          is_discoverable: false,
          users_discoverable: false,
          url: website,
          clearbit_domain: website,
        },
      });
      return data;
    } catch (err) {
      captureException(err);
      flashesStore.addUnhandledError(err);
    }
  }

  async function deletePartnership({ partner }) {
    try {
      const deletePartnershipUrl = urls.partners.deletePartnership(
        partner.uuid,
      );
      await axios.delete(deletePartnershipUrl);
      flashesStore.addSuccessFlash({
        message: `${partner.name} is no longer a partner`,
        description: 'It will take a few minutes to fully delete their data',
      });
      await partnersStore.refreshAfterPartnershipDeletion();
      return true;
    } catch (err) {
      captureException(err);
      flashesStore.addErrorFlash(
        'We were not able to delete your partnership, please try again',
      );
      return false;
    }
  }

  return {
    deletePartnership,
    getDiscoverableTeam,
    sendInvitation,
    createOfflinePartner,
  };
}
